// src/components/pages/Geral/HistoricoRelatorios.js
//
// Histórico de relatórios mensais. Antes só existia RelatorioMensal.js,
// travado no mês atual — não tinha como ver um relatório de um mês
// passado sem trocar a data do computador. Aqui:
//   1. Lista todos os meses/ano que já têm relatório gerado (mais
//      recente primeiro), com quantos dias foram fechados em cada um.
//   2. Ao clicar num mês, mostra as mesmas tabelas de
//      Crescimento/Frequência do relatório mensal, só que para o
//      mês/ano escolhido — reaproveita RelatorioMensalTabelas.js e
//      montarCrescimentoDoMes (extraídos de RelatorioMensal.js).

import { useEffect, useState } from 'react'
import { useAuth } from '../../../hooks/AuthContext'
import { useToast } from '../../../hooks/Toast'
import { listarMesesComRelatorio, buscarRelatoriosDoMes, montarCrescimentoDoMes } from '../../../firebase/relatorios'
import RelatorioMensalTabelas from '../../layout/RelatorioMensalTabelas'
import { ArrowLeft } from 'lucide-react'
import style from './geral.module.css'

const NOMES_MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

export default function HistoricoRelatorios() {
  const { usuario } = useAuth()
  const { mostrarAviso } = useToast()

  const [meses, setMeses] = useState(null)
  const [carregandoLista, setCarregandoLista] = useState(true)

  const [mesSelecionado, setMesSelecionado] = useState(null) // { mes, ano } | null
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false)
  const [diasOrdenados, setDiasOrdenados] = useState(null)
  const [crescimento, setCrescimento] = useState(null)

  useEffect(() => {
    listarMesesComRelatorio(usuario.idEscola)
      .then(setMeses)
      .catch((error) => {
        console.error('Erro ao listar histórico de relatórios:', error)
        mostrarAviso('Erro ao carregar o histórico de relatórios', 'erro')
      })
      .finally(() => setCarregandoLista(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const abrirMes = async (mes, ano) => {
    setMesSelecionado({ mes, ano })
    setCarregandoDetalhe(true)
    setDiasOrdenados(null)
    setCrescimento(null)
    try {
      const docs = await buscarRelatoriosDoMes(usuario.idEscola, mes, ano)
      const resultado = montarCrescimentoDoMes(docs)
      if (resultado) {
        setDiasOrdenados(resultado.diasOrdenados)
        setCrescimento(resultado.crescimento)
      }
    } catch (error) {
      mostrarAviso('Erro ao carregar o relatório desse mês: ' + error.message, 'erro')
    } finally {
      setCarregandoDetalhe(false)
    }
  }

  const voltarParaLista = () => {
    setMesSelecionado(null)
    setDiasOrdenados(null)
    setCrescimento(null)
  }

  // --- Tela de detalhe de um mês específico -------------------------------
  if (mesSelecionado) {
    return (
      <section className={`${style.container}`}>
        <div
          onClick={voltarParaLista}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '12px', width: 'fit-content' }}
        >
          <ArrowLeft size={20} /> <span>Voltar ao histórico</span>
        </div>
        <h3>{NOMES_MESES[mesSelecionado.mes - 1]} de {mesSelecionado.ano}</h3>

        {carregandoDetalhe && <p>Carregando relatório...</p>}

        {!carregandoDetalhe && !diasOrdenados && (
          <p>Nenhum relatório encontrado para este mês.</p>
        )}

        {!carregandoDetalhe && diasOrdenados && (
          <div className={`${style.geralList}`}>
            <RelatorioMensalTabelas
              diasOrdenados={diasOrdenados}
              crescimento={crescimento}
              mes={mesSelecionado.mes}
            />
          </div>
        )}
      </section>
    )
  }

  // --- Tela de lista (histórico) ------------------------------------------
  return (
    <section className={`${style.container}`}>
      <h3>Histórico de relatórios mensais</h3>

      {carregandoLista && <p>Carregando histórico...</p>}

      {!carregandoLista && meses?.length === 0 && (
        <p>Nenhum relatório mensal foi gerado ainda. Feche a chamada das aulas e gere o relatório do mês em "Gerar relatório mensal".</p>
      )}

      {!carregandoLista && meses?.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px', marginTop: '16px' }}>
          {meses.map((m) => (
            <div
              key={`${m.ano}-${m.mes}`}
              onClick={() => abrirMes(m.mes, m.ano)}
              style={{
                cursor: 'pointer',
                border: '1px solid #e5e5e5',
                borderRadius: '10px',
                padding: '16px',
                textAlign: 'center',
              }}
            >
              <strong>{NOMES_MESES[m.mes - 1]}</strong>
              <p style={{ margin: '4px 0 0', color: '#666' }}>{m.ano}</p>
              <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#999' }}>
                {m.quantidade} dia{m.quantidade === 1 ? '' : 's'} registrado{m.quantidade === 1 ? '' : 's'}
              </p>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
